import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchActivePresence } from '../api';

function hashToIndex(value, mod) {
  const s = String(value ?? '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  const n = Math.abs(h);
  return mod ? (n % mod) : 0;
}

function initialsFromName(name) {
  const raw = String(name || '').trim();
  if (!raw) return '?';
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

const COLOR_CLASSES = [
  'bg-indigo-600',
  'bg-emerald-600',
  'bg-amber-600',
  'bg-rose-600',
  'bg-cyan-600',
  'bg-violet-600',
];

function isHiddenPresenceUser(user) {
  const name = String(user?.name || '').trim().toLowerCase();
  if (!name) return false;
  // Hide seeded/demo accounts from the active presence UI.
  return name === 'admin test'
    || name === 'manager test'
    || name === 'admin and manager test'
    || name === 'employee test';
}

export default function ActiveUsersAvatars({ minutes = 5, maxVisible = 6, pollMs = 30000 }) {
  const [active, setActive] = useState([]);
  const [hidden, setHidden] = useState(false);
  const timerRef = useRef(null);
  const mountedRef = useRef(false);

  const params = useMemo(() => {
    const m = Number(minutes);
    const clamped = Number.isFinite(m) ? Math.min(240, Math.max(1, Math.floor(m))) : 5;
    const mv = Number(maxVisible);
    const visible = Number.isFinite(mv) ? Math.max(1, Math.floor(mv)) : 6;
    const p = Number(pollMs);
    const poll = Number.isFinite(p) ? Math.max(5000, Math.floor(p)) : 30000;
    return { clamped, visible, poll };
  }, [minutes, maxVisible, pollMs]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    function onLogout() {
      setActive([]);
    }
    window.addEventListener('auth:logout', onLogout);
    return () => window.removeEventListener('auth:logout', onLogout);
  }, []);

  useEffect(() => {
    if (hidden) return undefined;

    let cancelled = false;

    async function loadOnce() {
      try {
        const data = await fetchActivePresence({ minutes: params.clamped });
        if (cancelled || !mountedRef.current) return;
        const list = Array.isArray(data?.active) ? data.active : [];
        setActive(list.filter(u => !isHiddenPresenceUser(u)));
      } catch (err) {
        if (cancelled || !mountedRef.current) return;
        if (err && err.status === 401) {
          setActive([]);
          setHidden(true);
          return;
        }
        if (err && err.status === 403) {
          setHidden(true);
          setActive([]);
        }
      }
    }

    loadOnce();

    timerRef.current = setInterval(loadOnce, params.poll);
    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [hidden, params.clamped, params.poll]);

  if (hidden) return null;

  const visibleUsers = active.slice(0, params.visible);
  const overflow = Math.max(0, active.length - visibleUsers.length);

  if (visibleUsers.length === 0 && overflow === 0) return null;

  return (
    <div className="flex items-center gap-1" aria-label="Active users">
      {visibleUsers.map((u) => {
        const key = u?.id ?? u?.name ?? Math.random();
        const name = u?.name || 'Unknown';
        const role = u?.role ? String(u.role) : '';
        const title = role ? `${name} • ${role}` : name;
        const idx = hashToIndex(u?.id ?? u?.name, COLOR_CLASSES.length);
        const initials = initialsFromName(name);
        return (
          <div
            key={key}
            title={title}
            aria-label={title}
            className={
              `inline-flex items-center justify-center w-8 h-8 rounded-full ` +
              `${COLOR_CLASSES[idx]} text-white text-xs font-semibold select-none`
            }
          >
            {initials}
          </div>
        );
      })}
      {overflow > 0 && (
        <div
          title={`+${overflow} more`}
          aria-label={`+${overflow} more active users`}
          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 text-xs font-semibold select-none"
        >
          +{overflow}
        </div>
      )}
    </div>
  );
}
